import { useState, useEffect, useCallback } from "react";
import { useAuth } from "react-oidc-context";

const API_URL = process.env.REACT_APP_API_URL || "/api";

export const useGreenMilesDemos = () => {
  const auth = useAuth();
  const token = auth.user?.access_token;

  const [demos, setDemos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortState, setSortState] = useState({
    column: "hTimeCreate",
    direction: "desc",
  });

  const fetchDemos = useCallback(async () => {
    if (!token) return;

    try {
      setLoading(true);
      setError(null);

      const response = await fetch(`${API_URL}/greenmiles/demos`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.success === "OK") {
        const result = data.result || [];
        setDemos(Array.isArray(result) ? result : []);
      } else {
        throw new Error(data.message || "Erreur lors de la récupération des démos GreenMiles");
      }
    } catch (err) {
      console.error("Error fetching GreenMiles demos:", err);
      setError(err.message || "Erreur lors de la récupération des données");
      setDemos([]);
    } finally {
      setLoading(false);
    }
  }, [token]);

  const fetchTemplates = useCallback(async () => {
    if (!token) return { actions: [], rewards: [] };
    try {
      const resp = await fetch(`${API_URL}/greenmiles/templates`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) return { actions: [], rewards: [] };
      const data = await resp.json();
      return data.result || { actions: [], rewards: [] };
    } catch (e) {
      return { actions: [], rewards: [] };
    }
  }, [token]);

  useEffect(() => {
    fetchDemos();
  }, [fetchDemos]);

  const handleSort = useCallback((column) => {
    setSortState((prevState) => {
      const newDirection = prevState.column === column && prevState.direction === "asc" ? "desc" : "asc";
      return { column, direction: newDirection };
    });
  }, []);

  const sortedDemos = Array.isArray(demos)
    ? [...demos].sort((a, b) => {
        const { column, direction } = sortState;
        let aValue, bValue;

        switch (column) {
          case "hName":
            aValue = a.hName?.toLowerCase() || "";
            bValue = b.hName?.toLowerCase() || "";
            break;
          case "hCity":
            aValue = a.hCity?.toLowerCase() || "";
            bValue = b.hCity?.toLowerCase() || "";
            break;
          case "hStars":
            aValue = a.hStars || 0;
            bValue = b.hStars || 0;
            break;
          case "hNbRooms":
            aValue = a.hNbRooms || 0;
            bValue = b.hNbRooms || 0;
            break;
          case "qr_rooms_count":
            aValue = a.qr_rooms_count || 0;
            bValue = b.qr_rooms_count || 0;
            break;
          case "hTimeCreate":
            aValue = new Date(a.hTimeCreate || 0);
            bValue = new Date(b.hTimeCreate || 0);
            break;
          default:
            aValue = a[column] || "";
            bValue = b[column] || "";
        }

        if (aValue instanceof Date && bValue instanceof Date) {
          return direction === "asc" ? aValue - bValue : bValue - aValue;
        } else if (typeof aValue === "string" && typeof bValue === "string") {
          return direction === "asc" ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
        } else {
          return direction === "asc" ? aValue - bValue : bValue - aValue;
        }
      })
    : [];

  return {
    demos: sortedDemos,
    loading,
    error,
    sortState,
    onSort: handleSort,
    handleSort,
    fetchTemplates,
    fetchDemos,
    refetch: fetchDemos,
  };
};
